import React, { useState, useEffect } from 'react';
import { collection, query, where, getDocs, addDoc, doc, updateDoc, deleteDoc, orderBy, limit } from 'firebase/firestore';
import { db, auth } from './firebase';
import './TermStructureSetup.css';

// ============================================
// HELPER FUNCTIONS FOR CALCULATING PUBLIC HOLIDAYS
// ============================================

/**
 * Calculate Easter Sunday using Computus algorithm
 */
const calculateEaster = (year) => {
  const a = year % 19;
  const b = Math.floor(year / 100);
  const c = year % 100;
  const d = Math.floor(b / 4);
  const e = b % 4;
  const f = Math.floor((b + 8) / 25);
  const g = Math.floor((b - f + 1) / 3);
  const h = (19 * a + b - d - g + 15) % 30;
  const i = Math.floor(c / 4);
  const k = c % 4;
  const l = (32 + 2 * e + 2 * i - h - k) % 7;
  const m = Math.floor((a + 11 * h + 22 * l) / 451);
  const month = Math.floor((h + l - 7 * m + 114) / 31);
  const day = ((h + l - 7 * m + 114) % 31) + 1;
  
  return new Date(year, month - 1, day);
};

/**
 * Add days to a date
 */
const addDaysToDate = (date, days) => {
  const result = new Date(date);
  result.setDate(result.getDate() + days);
  return result;
};

/**
 * Format date as YYYY-MM-DD
 */
const formatDateString = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

/**
 * Adjust date if it falls on Sunday (move to Monday)
 */
const adjustForSunday = (date) => {
  const dayOfWeek = date.getDay();
  if (dayOfWeek === 0) { // Sunday
    return addDaysToDate(date, 1);
  }
  return date;
};

/**
 * Calculate all South African public holidays for a given year
 */
const calculateSAPublicHolidays = (year) => {
  const holidays = [];
  
  // Fixed date holidays
  const fixedHolidays = [
    { name: "New Year's Day", month: 1, day: 1 },
    { name: "Human Rights Day", month: 3, day: 21 },
    { name: "Freedom Day", month: 4, day: 27 },
    { name: "Workers' Day", month: 5, day: 1 },
    { name: "Youth Day", month: 6, day: 16 },
    { name: "National Women's Day", month: 8, day: 9 },
    { name: "Heritage Day", month: 9, day: 24 },
    { name: "Day of Reconciliation", month: 12, day: 16 },
    { name: "Christmas Day", month: 12, day: 25 },
    { name: "Day of Goodwill", month: 12, day: 26 }
  ];
  
  // Add fixed holidays with Sunday adjustment
  fixedHolidays.forEach(holiday => {
    let holidayDate = new Date(year, holiday.month - 1, holiday.day);
    holidayDate = adjustForSunday(holidayDate);
    
    holidays.push({
      name: holiday.name,
      date: formatDateString(holidayDate)
    });
  });
  
  // Calculate Easter-based holidays
  const easter = calculateEaster(year);
  
  // Good Friday (2 days before Easter)
  const goodFriday = addDaysToDate(easter, -2);
  holidays.push({
    name: "Good Friday",
    date: formatDateString(goodFriday)
  });
  
  // Family Day (Monday after Easter)
  const familyDay = addDaysToDate(easter, 1);
  holidays.push({
    name: "Family Day",
    date: formatDateString(familyDay)
  });
  
  // Sort holidays by date
  holidays.sort((a, b) => new Date(a.date) - new Date(b.date));
  
  return holidays;
};

// ============================================
// MAIN COMPONENT
// ============================================

function TermStructureSetup({ onClose }) {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [marketingOrderFees, setMarketingOrderFees] = useState(null);
  
  // Holiday detection states
  const [showHolidayReview, setShowHolidayReview] = useState(false);
  const [detectedHolidays, setDetectedHolidays] = useState([]);
  const [selectedPublicHolidays, setSelectedPublicHolidays] = useState([]);
  const [publicHolidays, setPublicHolidays] = useState([]);

  const [formData, setFormData] = useState({
    templateName: '',
    templateDescription: '',
    termType: 4,
    linkedToMarketingOrder: '',
    lessonsPerTerm: '',
    term1Start: '',
    term1End: '',
    term2Start: '',
    term2End: '',
    term3Start: '',
    term3End: '',
    term4Start: '',
    term4End: '',
    notes: ''
  });

  useEffect(() => {
    loadTemplates();
    loadMarketingOrderFees();
  }, []);

  useEffect(() => {
    // Calculate holidays for next year
    const nextYear = new Date().getFullYear();
    const holidays = calculateSAPublicHolidays(nextYear);
    setPublicHolidays(holidays);
    console.log(`📅 Calculated ${holidays.length} public holidays for ${nextYear}`);
  }, []);

  const loadMarketingOrderFees = async () => {
    try {
      const user = auth.currentUser;
      const franchiseeRef = doc(db, 'Franchisees', user.uid);
      const q = query(
        collection(db, 'marketingOrders'),
        where('franchiseeRef', '==', franchiseeRef),
        orderBy('orderDate', 'desc'),
        limit(1)
      );
      const snapshot = await getDocs(q);
      if (!snapshot.empty) {
        const orderData = snapshot.docs[0].data();
        setMarketingOrderFees({
          c1: {
            termFee: parseFloat(orderData.c1TermFee) || 0,
            instalmentFee: parseFloat(orderData.c1InstalmentFee) || 0
          },
          c2: {
            termFee: parseFloat(orderData.c2TermFee) || 0,
            instalmentFee: parseFloat(orderData.c2InstalmentFee) || 0
          },
          c3: {
            termFee: parseFloat(orderData.c3TermFee) || 0,
            instalmentFee: parseFloat(orderData.c3InstalmentFee) || 0
          }
        });
      }
    } catch (error) {
      console.error('Error loading marketing order fees:', error);
    }
  };

  const loadTemplates = async () => {
    try {
      const user = auth.currentUser;
      const franchiseeRef = doc(db, 'Franchisees', user.uid);
      const q = query(
        collection(db, 'configurations'),
        where('franchiseeRef', '==', franchiseeRef),
        where('configType', '==', 'termStructure'),
        where('isActive', '==', true)
      );
      const snapshot = await getDocs(q);
      const templatesList = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setTemplates(templatesList);
    } catch (error) {
      console.error('Error loading templates:', error);
      alert('Error loading term structure templates');
    } finally {
      setLoading(false);
    }
  };

  const detectHolidayGaps = () => {
    const gaps = [];
    const currentYear = new Date().getFullYear() // Always create for next year
    
    console.log('🔍 DEBUG: Starting holiday detection for year:', currentYear);

    // Helper to parse MM-DD format to Date (in LOCAL timezone)
    const parseTermDate = (mmdd) => {
      const [month, day] = mmdd.split('-').map(Number);
      const date = new Date(currentYear, month - 1, day);
      date.setHours(0, 0, 0, 0);
      console.log(`  📅 Parsed ${mmdd} to:`, date.toDateString());
      return date;
    };

    // Helper to format date as YYYY-MM-DD in LOCAL timezone (not UTC!)
    const formatLocalDate = (date) => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    };

    // Helper to add days to a date
    const addDays = (date, days) => {
      const result = new Date(date);
      result.setDate(result.getDate() + days);
      result.setHours(0, 0, 0, 0);
      return result;
    };

    // Helper to calculate days between dates (inclusive)
    const daysBetween = (date1, date2) => {
      const d1 = new Date(date1);
      const d2 = new Date(date2);
      d1.setHours(0, 0, 0, 0);
      d2.setHours(0, 0, 0, 0);
      
      const diffTime = d2.getTime() - d1.getTime();
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      return diffDays + 1;
    };

    // Collect all term dates
    const terms = [];
    
    console.log('📋 Term data from form:', {
      term1: `${formData.term1Start} - ${formData.term1End}`,
      term2: `${formData.term2Start} - ${formData.term2End}`,
      term3: `${formData.term3Start} - ${formData.term3End}`,
      term4: formData.termType === 4 ? `${formData.term4Start} - ${formData.term4End}` : 'N/A'
    });
    
    if (formData.term1Start && formData.term1End) {
      const start = parseTermDate(formData.term1Start);
      const end = parseTermDate(formData.term1End);
      terms.push({ number: 1, start, end });
      console.log('✅ Term 1:', start.toDateString(), 'to', end.toDateString());
    }
    
    if (formData.term2Start && formData.term2End) {
      const start = parseTermDate(formData.term2Start);
      const end = parseTermDate(formData.term2End);
      terms.push({ number: 2, start, end });
      console.log('✅ Term 2:', start.toDateString(), 'to', end.toDateString());
    }
    
    if (formData.term3Start && formData.term3End) {
      const start = parseTermDate(formData.term3Start);
      const end = parseTermDate(formData.term3End);
      terms.push({ number: 3, start, end });
      console.log('✅ Term 3:', start.toDateString(), 'to', end.toDateString());
    }
    
    if (formData.termType === 4 && formData.term4Start && formData.term4End) {
      const start = parseTermDate(formData.term4Start);
      const end = parseTermDate(formData.term4End);
      terms.push({ number: 4, start, end });
      console.log('✅ Term 4:', start.toDateString(), 'to', end.toDateString());
    }

    console.log(`\n🔄 Detecting gaps between ${terms.length} terms...\n`);

    // Find gaps between consecutive terms
    for (let i = 0; i < terms.length - 1; i++) {
      const currentTerm = terms[i];
      const nextTerm = terms[i + 1];
      
      console.log(`\n--- Analyzing gap between Term ${currentTerm.number} and Term ${nextTerm.number} ---`);
      console.log(`  Current term ends: ${currentTerm.end.toDateString()}`);
      console.log(`  Next term starts: ${nextTerm.start.toDateString()}`);
      
      // Break starts the day AFTER current term ends
      const breakStart = addDays(currentTerm.end, 1);
      console.log(`  Break should start: ${breakStart.toDateString()} (day after term ends)`);
      
      // Break ends the day BEFORE next term starts  
      const breakEnd = addDays(nextTerm.start, -1);
      console.log(`  Break should end: ${breakEnd.toDateString()} (day before next term)`);
      
      // Only create gap if there's at least 1 day between terms
      if (breakStart <= breakEnd) {
        const dayCount = daysBetween(breakStart, breakEnd);
        console.log(`  ✅ GAP DETECTED: ${dayCount} days`);
        
        const gap = {
          name: `Term ${currentTerm.number} Break`,
          startDate: formatLocalDate(breakStart),
          endDate: formatLocalDate(breakEnd),
          dayCount: dayCount,
          isSingleDay: dayCount === 1,
          holidayType: 'school',
          autoDetected: true,
          selected: true
        };
        
        console.log('  📦 Created gap:', gap);
        gaps.push(gap);
      } else {
        console.log(`  ❌ NO GAP: Terms are consecutive or overlapping`);
      }
    }

    // Add year-end break
    if (terms.length > 0) {
      console.log(`\n--- Creating Year End Break ---`);
      const lastTerm = terms[terms.length - 1];
      const yearEndStart = addDays(lastTerm.end, 1);
      const yearEndEnd = new Date(currentYear + 1, 0, 14);
      yearEndEnd.setHours(0, 0, 0, 0);
      
      console.log(`  Last term ends: ${lastTerm.end.toDateString()}`);
      console.log(`  Year end break starts: ${yearEndStart.toDateString()}`);
      console.log(`  Year end break ends: ${yearEndEnd.toDateString()}`);
      
      const dayCount = daysBetween(yearEndStart, yearEndEnd);
      console.log(`  Total days: ${dayCount}`);
      
      gaps.push({
        name: 'Year End Break',
        startDate: formatLocalDate(yearEndStart),
        endDate: formatLocalDate(yearEndEnd),
        dayCount: dayCount,
        isSingleDay: false,
        holidayType: 'school',
        autoDetected: true,
        selected: true
      });
    }

    console.log('\n✅ Final detected holidays:', gaps);
    console.log('-----------------------------------\n');

    return gaps;
  };

  const checkPublicHolidayOverlap = (publicHolidayDate) => {
    const holidayDate = new Date(publicHolidayDate);
    
    for (const termBreak of detectedHolidays) {
      const breakStart = new Date(termBreak.startDate);
      const breakEnd = new Date(termBreak.endDate);
      
      if (holidayDate >= breakStart && holidayDate <= breakEnd) {
        return termBreak.name;
      }
    }
    return null;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleTermTypeChange = (termType) => {
    setFormData(prev => ({
      ...prev,
      termType: termType
    }));
  };

  const handleAddNew = () => {
    setEditingTemplate(null);
    setFormData({
      templateName: '',
      templateDescription: '',
      termType: 4,
      linkedToMarketingOrder: '',
      lessonsPerTerm: '',
      term1Start: '',
      term1End: '',
      term2Start: '',
      term2End: '',
      term3Start: '',
      term3End: '',
      term4Start: '',
      term4End: '',
      notes: ''
    });
    setShowModal(true);
  };

  const handleEdit = (template) => {
    setEditingTemplate(template);
    
    if (!template.termDates) {
      alert('⚠️ This template is missing term dates. Please add them now.');
    }
    
    const term1 = template.termDates?.term1 || {};
    const term2 = template.termDates?.term2 || {};
    const term3 = template.termDates?.term3 || {};
    const term4 = template.termDates?.term4 || {};

    setFormData({
      templateName: template.templateName || '',
      templateDescription: template.templateDescription || '',
      termType: template.termType || 4,
      linkedToMarketingOrder: template.linkedToMarketingOrder || '',
      lessonsPerTerm: template.lessonsPerTerm || 10,
      term1Start: term1.start || '',
      term1End: term1.end || '',
      term2Start: term2.start || '',
      term2End: term2.end || '',
      term3Start: term3.start || '',
      term3End: term3.end || '',
      term4Start: term4.start || '',
      term4End: term4.end || '',
      notes: template.notes || ''
    });
    setShowModal(true);
  };

  const handleContinueToHolidays = (e) => {
    e.preventDefault();
    
    // Validate term dates first
    if (!formData.term1Start || !formData.term1End || 
        !formData.term2Start || !formData.term2End || 
        !formData.term3Start || !formData.term3End) {
      alert('Please fill in all term dates for Terms 1-3');
      return;
    }

    if (formData.termType === 4 && (!formData.term4Start || !formData.term4End)) {
      alert('Please fill in term dates for Term 4');
      return;
    }

    // Detect holiday gaps
    const gaps = detectHolidayGaps();
    setDetectedHolidays(gaps);
    
    // Hide term form, show holiday review
    setShowModal(false);
    setShowHolidayReview(true);
  };

  const toggleDetectedHoliday = (index) => {
    setDetectedHolidays(prev => prev.map((holiday, i) => 
      i === index ? { ...holiday, selected: !holiday.selected } : holiday
    ));
  };

  const togglePublicHoliday = (date) => {
    setSelectedPublicHolidays(prev => 
      prev.includes(date) 
        ? prev.filter(d => d !== date)
        : [...prev, date]
    );
  };

  const selectAllPublicHolidays = () => {
    if (selectedPublicHolidays.length === publicHolidays.length) {
      setSelectedPublicHolidays([]);
    } else {
      setSelectedPublicHolidays(publicHolidays.map(h => h.date));
    }
  };

  const handleFinalSubmit = async () => {
    setLoading(true);

    try {
      const user = auth.currentUser;
      const franchiseeRef = doc(db, 'Franchisees', user.uid);

      // Build term dates object
      const termDates = {
        term1: {
          start: formData.term1Start,
          end: formData.term1End
        },
        term2: {
          start: formData.term2Start,
          end: formData.term2End
        },
        term3: {
          start: formData.term3Start,
          end: formData.term3End
        }
      };

      if (formData.termType === 4) {
        termDates.term4 = {
          start: formData.term4Start,
          end: formData.term4End
        };
      }

      // Save term structure
      const templateData = {
        configType: 'termStructure',
        franchiseeRef: franchiseeRef,
        templateName: formData.templateName,
        templateDescription: formData.templateDescription,
        termType: parseInt(formData.termType),
        termDates: termDates,
        linkedToMarketingOrder: formData.linkedToMarketingOrder || '',
        lessonsPerTerm: parseInt(formData.lessonsPerTerm) || 10,
        notes: formData.notes,
        isActive: true
      };

      let termStructureRef;
      if (editingTemplate) {
        await updateDoc(doc(db, 'configurations', editingTemplate.id), {
          ...templateData,
          lastModified: new Date(),
          modifiedBy: user.email
        });
        termStructureRef = doc(db, 'configurations', editingTemplate.id);
      } else {
        const newDoc = await addDoc(collection(db, 'configurations'), {
          ...templateData,
          createdDate: new Date(),
          createdBy: user.email
        });
        termStructureRef = newDoc;
      }

      // Save selected term break holidays
      for (const holiday of detectedHolidays) {
        if (holiday.selected) {
          await addDoc(collection(db, 'holidays'), {
            franchiseeRef: franchiseeRef,
            schoolRef: null,
            holidayName: holiday.name,
            holidayType: holiday.holidayType,
            startDate: new Date(holiday.startDate + 'T00:00:00'),
            endDate: new Date(holiday.endDate + 'T00:00:00'),
            isSingleDay: holiday.isSingleDay,
            appliesToAllSchools: true,
            autoDetected: true,
            linkedTermStructureRef: termStructureRef,
            notes: `Auto-generated from ${formData.templateName}`,
            createdDate: new Date(),
            createdBy: user.email
          });
        }
      }

      // Save selected public holidays
      // ✅ FIX: Only save public holidays if they DON'T already exist
      // Check existing public holidays first
      const existingHolidaysQuery = query(
        collection(db, 'holidays'),
        where('franchiseeRef', '==', franchiseeRef),
        where('holidayType', '==', 'public')
      );
      const existingHolidaysSnapshot = await getDocs(existingHolidaysQuery);
      const existingPublicHolidayDates = existingHolidaysSnapshot.docs.map(doc => {
        const data = doc.data();
        const date = data.startDate.toDate ? data.startDate.toDate() : new Date(data.startDate);
        return date.toISOString().split('T')[0];
      });

      // Only add public holidays that don't already exist
      let addedPublicHolidays = 0;
      for (const holidayDate of selectedPublicHolidays) {
        // Check if this date already exists
        if (!existingPublicHolidayDates.includes(holidayDate)) {
          const holiday = publicHolidays.find(h => h.date === holidayDate);
          
          await addDoc(collection(db, 'holidays'), {
            franchiseeRef: franchiseeRef,
            schoolRef: null,
            holidayName: holiday.name,
            holidayType: 'public',
            startDate: new Date(holiday.date + 'T00:00:00'),
            endDate: new Date(holiday.date + 'T00:00:00'),
            isSingleDay: true,
            appliesToAllSchools: true,
            autoDetected: false,
            linkedTermStructureRef: null,
            notes: 'Public holiday - no lessons',
            createdDate: new Date(),
            createdBy: user.email
          });
          addedPublicHolidays++;
        }
      }

      const totalHolidays = detectedHolidays.filter(h => h.selected).length + addedPublicHolidays;
      const skippedPublicHolidays = selectedPublicHolidays.length - addedPublicHolidays;
      
      let message = `✅ Term structure ${editingTemplate ? 'updated' : 'created'} successfully!\n${totalHolidays} holidays added.`;
      if (skippedPublicHolidays > 0) {
        message += `\n${skippedPublicHolidays} public holiday(s) already existed and were not duplicated.`;
      }
      alert(message);
      // Reset and reload
      setShowHolidayReview(false);
      setDetectedHolidays([]);
      setSelectedPublicHolidays([]);
      loadTemplates();
    } catch (error) {
      console.error('Error saving:', error);
      alert('Error saving term structure and holidays');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (templateId) => {
    if (!window.confirm('Are you sure you want to delete this template?')) {
      return;
    }

    try {
      await deleteDoc(doc(db, 'configurations', templateId));
      alert('Template deleted successfully!');
      loadTemplates();
    } catch (error) {
      console.error('Error deleting template:', error);
      alert('Error deleting template');
    }
  };

  if (loading && templates.length === 0) {
    return (
      <div className="term-structure-container">
        <div className="loading">Loading templates...</div>
      </div>
    );
  }

  return (
    <div className="term-structure-container">
      {/* Header */}
      <div className="term-header">
        <div className="header-left">
          <h1>⚙️ Term Structure Templates</h1>
          <p>Manage term dates for your schools</p>
        </div>
      </div>

      {/* Add New Button */}
      <button onClick={handleAddNew} className="btn-add-template">
        + Add New Template
      </button>

      {/* Templates List */}
      <div className="templates-grid">
        {templates.map(template => (
          <div key={template.id} className="template-card">
            <div className="template-header">
              <h3>{template.templateName}</h3>
              <span className="term-badge">
                {template.termType} Terms
              </span>
              {template.lessonsPerTerm && (
                <span className="lessons-badge">
                  📚 {template.lessonsPerTerm} LESSONS
                </span>
              )}
              {template.linkedToMarketingOrder && (
                <span className={`package-badge ${template.linkedToMarketingOrder}`}>
                  {template.linkedToMarketingOrder.toUpperCase()}
                </span>
              )}
            </div>

            {marketingOrderFees && template.linkedToMarketingOrder && (
              <div className="linked-fees">
                💰 <strong>Linked Fees:</strong> Term Fee R
                {marketingOrderFees[template.linkedToMarketingOrder]?.termFee || 0} | Instalment R
                {marketingOrderFees[template.linkedToMarketingOrder]?.instalmentFee || 0}
              </div>
            )}

            <div className="term-dates">
              {Object.entries(template.termDates || {})
                .sort(([keyA], [keyB]) => {
                  const numA = parseInt(keyA.replace('term', ''));
                  const numB = parseInt(keyB.replace('term', ''));
                  return numA - numB;
                })
                .map(([termKey, termData]) => (
                  <div key={termKey} className="term-date-row">
                    <strong>{termKey.replace('term', 'Term ')}:</strong>
                    <span>{termData.start} - {termData.end}</span>
                  </div>
                ))}
            </div>

            <div className="template-actions">
              <button onClick={() => handleEdit(template)} className="btn-edit">
                ✏️ Edit
              </button>
              <button 
                onClick={() => handleDelete(template.id)} 
                className="btn-delete"
                disabled={template.usedBySchoolCount > 0}
              >
                🗑️ Delete
              </button>
            </div>

            <div className="template-usage">
              🎓 Used by {template.usedBySchoolCount || 0} school(s)
            </div>
          </div>
        ))}
      </div>

      {/* Term Structure Form Modal - STEP 1 */}
      {showModal && (
        <div className="modal-overlay">
          <div className="modal-content-large">
            <div className="modal-header">
              <h2>{editingTemplate ? 'Edit Template' : 'Create New Term Structure Template'}</h2>
              <button onClick={() => setShowModal(false)} className="modal-close">✕</button>
            </div>

            <form onSubmit={handleContinueToHolidays} className="template-form">
              <div className="form-section">
                <h3>Template Information</h3>
                
                <div className="form-group">
                  <label>Template Name *</label>
                  <input
                    type="text"
                    name="templateName"
                    value={formData.templateName}
                    onChange={handleChange}
                    required
                    placeholder="e.g., 4 Term Schools (2026)"
                  />
                </div>

                <div className="form-group">
                  <label>Description</label>
                  <input
                    type="text"
                    name="templateDescription"
                    value={formData.templateDescription}
                    onChange={handleChange}
                    placeholder="Optional description"
                  />
                </div>

                <div className="form-group">
                  <label>Lessons Per Term</label>
                  <input
                    type="number"
                    name="lessonsPerTerm"
                    value={formData.lessonsPerTerm}
                    onChange={handleChange}
                    placeholder="e.g., 10"
                    min="1"
                    max="20"
                  />
                  <small>Number of lessons per term (typically 8-10)</small>
                </div>
              </div>

              <div className="form-section">
                <h3>🗓️ Term Type</h3>
                <div className="radio-group">
                  <label className="radio-label">
                    <input
                      type="radio"
                      checked={formData.termType === 4}
                      onChange={() => handleTermTypeChange(4)}
                    />
                    <span>4 Terms per year</span>
                  </label>
                  <label className="radio-label">
                    <input
                      type="radio"
                      checked={formData.termType === 3}
                      onChange={() => handleTermTypeChange(3)}
                    />
                    <span>3 Terms per year</span>
                  </label>
                </div>
              </div>

              <div className="form-section">
                <h3>📅 Term Dates</h3>
                <p className="section-note">Enter dates in MM-DD format (e.g., 01-15 for January 15th)</p>

                {/* Term 1 */}
                <div className="term-date-inputs">
                  <h4>Term 1 *</h4>
                  <div className="date-row">
                    <div className="form-group">
                      <label>Start Date (MM-DD)</label>
                      <input
                        type="text"
                        name="term1Start"
                        value={formData.term1Start}
                        onChange={handleChange}
                        required
                        placeholder="01-15"
                        pattern="[0-1][0-9]-[0-3][0-9]"
                      />
                    </div>
                    <div className="form-group">
                      <label>End Date (MM-DD)</label>
                      <input
                        type="text"
                        name="term1End"
                        value={formData.term1End}
                        onChange={handleChange}
                        required
                        placeholder="03-31"
                        pattern="[0-1][0-9]-[0-3][0-9]"
                      />
                    </div>
                  </div>
                </div>

                {/* Term 2 */}
                <div className="term-date-inputs">
                  <h4>Term 2 *</h4>
                  <div className="date-row">
                    <div className="form-group">
                      <label>Start Date (MM-DD)</label>
                      <input
                        type="text"
                        name="term2Start"
                        value={formData.term2Start}
                        onChange={handleChange}
                        required
                        placeholder="04-15"
                        pattern="[0-1][0-9]-[0-3][0-9]"
                      />
                    </div>
                    <div className="form-group">
                      <label>End Date (MM-DD)</label>
                      <input
                        type="text"
                        name="term2End"
                        value={formData.term2End}
                        onChange={handleChange}
                        required
                        placeholder="06-30"
                        pattern="[0-1][0-9]-[0-3][0-9]"
                      />
                    </div>
                  </div>
                </div>

                {/* Term 3 */}
                <div className="term-date-inputs">
                  <h4>Term 3 *</h4>
                  <div className="date-row">
                    <div className="form-group">
                      <label>Start Date (MM-DD)</label>
                      <input
                        type="text"
                        name="term3Start"
                        value={formData.term3Start}
                        onChange={handleChange}
                        required
                        placeholder="07-15"
                        pattern="[0-1][0-9]-[0-3][0-9]"
                      />
                    </div>
                    <div className="form-group">
                      <label>End Date (MM-DD)</label>
                      <input
                        type="text"
                        name="term3End"
                        value={formData.term3End}
                        onChange={handleChange}
                        required
                        placeholder="09-30"
                        pattern="[0-1][0-9]-[0-3][0-9]"
                      />
                    </div>
                  </div>
                </div>

                {/* Term 4 (conditional) */}
                {formData.termType === 4 && (
                  <div className="term-date-inputs">
                    <h4>Term 4 *</h4>
                    <div className="date-row">
                      <div className="form-group">
                        <label>Start Date (MM-DD)</label>
                        <input
                          type="text"
                          name="term4Start"
                          value={formData.term4Start}
                          onChange={handleChange}
                          required
                          placeholder="10-01"
                          pattern="[0-1][0-9]-[0-3][0-9]"
                        />
                      </div>
                      <div className="form-group">
                        <label>End Date (MM-DD)</label>
                        <input
                          type="text"
                          name="term4End"
                          value={formData.term4End}
                          onChange={handleChange}
                          required
                          placeholder="12-15"
                          pattern="[0-1][0-9]-[0-3][0-9]"
                        />
                      </div>
                    </div>
                  </div>
                )}
              </div>

              <div className="form-section">
                <div className="form-group">
                  <label>Notes (Optional)</label>
                  <textarea
                    name="notes"
                    value={formData.notes}
                    onChange={handleChange}
                    placeholder="Any additional notes..."
                    rows="2"
                  />
                </div>
              </div>

              <div className="modal-actions">
                <button type="button" onClick={() => setShowModal(false)} className="btn-cancel">
                  Cancel
                </button>
                <button type="submit" className="btn-save">
                  Continue to Review Holidays →
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Holiday Review Modal - STEP 2 */}
      {showHolidayReview && (
        <div className="modal-overlay">
          <div className="modal-content-large">
            <div className="modal-header">
              <h2>📅 Review Detected Holidays</h2>
              <button onClick={() => {
                setShowHolidayReview(false);
                setShowModal(true);
              }} className="modal-close">✕</button>
            </div>

            <div className="holiday-review-content">
              <p className="review-intro">
                Based on your term dates, we detected these breaks. Select which holidays to add:
              </p>

              {/* Detected Term Breaks */}
              <div className="detected-section">
                <h3>🏫 Detected Term Breaks</h3>
                {detectedHolidays.map((holiday, index) => (
                  <div key={index} className="holiday-review-item">
                    <label>
                      <input
                        type="checkbox"
                        checked={holiday.selected}
                        onChange={() => toggleDetectedHoliday(index)}
                      />
                      <div className="holiday-details">
                        <strong>{holiday.name}</strong>
                        <span className="holiday-dates">
                          {new Date(holiday.startDate).toLocaleDateString('en-ZA', { day: 'numeric', month: 'short', year: 'numeric' })}
                          {!holiday.isSingleDay && ` - ${new Date(holiday.endDate).toLocaleDateString('en-ZA', { day: 'numeric', month: 'short', year: 'numeric' })}`}
                          <span className="day-count">({holiday.dayCount} day{holiday.dayCount > 1 ? 's' : ''})</span>
                        </span>
                      </div>
                    </label>
                  </div>
                ))}
              </div>

              {/* Public Holidays */}
              <div className="public-holidays-section">
                <h3>🎉 {new Date().getFullYear()} Public Holidays</h3>
                <div className="select-all-row">
                  <label>
                    <input
                      type="checkbox"
                      checked={selectedPublicHolidays.length === publicHolidays.length}
                      onChange={selectAllPublicHolidays}
                    />
                    <strong>Select All</strong>
                  </label>
                </div>
                {publicHolidays.map(holiday => {
                  const overlap = checkPublicHolidayOverlap(holiday.date);
                  return (
                    <div key={holiday.date} className="holiday-review-item">
                      <label>
                        <input
                          type="checkbox"
                          checked={selectedPublicHolidays.includes(holiday.date)}
                          onChange={() => togglePublicHoliday(holiday.date)}
                        />
                        <div className="holiday-details">
                          <strong>{holiday.name}</strong>
                          <span className="holiday-dates">
                            {new Date(holiday.date).toLocaleDateString('en-ZA', { day: 'numeric', month: 'long', year: 'numeric' })}
                          </span>
                          {overlap && (
                            <span className="overlap-warning">
                              ⚠️ Falls within {overlap}
                            </span>
                          )}
                        </div>
                      </label>
                    </div>
                  );
                })}
              </div>

              <div className="modal-actions">
                <button 
                  type="button" 
                  onClick={() => {
                    setShowHolidayReview(false);
                    setShowModal(true);
                  }} 
                  className="btn-cancel"
                >
                  ← Back to Edit Dates
                </button>
                <button 
                  onClick={handleFinalSubmit} 
                  className="btn-save"
                  disabled={loading}
                >
                  {loading ? 'Saving...' : `Save & Create ${detectedHolidays.filter(h => h.selected).length + selectedPublicHolidays.length} Holidays`}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default TermStructureSetup;